import { useEffect, useState } from 'react';
// import Modal from 'react-responsive-modal';
import { useContentStore } from '../../../providers/stores/content-store';
import { DateSelector } from '../../common/DatePicker';
import { Textbox } from "react-inputs-validation";
import { IIndexable } from '../../common/types/CommonTypes';
import logger from 'debug';
import { DateService, NotificationService } from '../../../providers';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

interface EditableRate {
	cost: string;
	bookingCode: string;
	quantity: string;
	isFreeSell: boolean;
	isSoldOut: boolean;
	minNights: string;
}

interface EditableRateValidation {
	isCostValid: boolean,
	isBookingCodeValid: boolean,
	isQuantityValid: boolean,
	isMinNightsValid: boolean
}

interface ValidationOptions {
	name: string,
	value: boolean
}

export type CloseDialogOptions = { rate?: EditableRate, startDate?: Date, endDate?: Date };
type CloseDialogHandler = (opts?: CloseDialogOptions) => void;

export interface BulkEditModalProps {
	isOpen: boolean,
	startDate?: Date,
	endDate?: Date,
	closeDialog: CloseDialogHandler
}

const debug = logger('pricing:bulkEditModal');

const defaultRate: EditableRate = {
	cost: '',
	bookingCode: '',
	quantity: '',
	isFreeSell: false,
	isSoldOut: false,
	minNights: ''
};

const BulkEditModal = (props: BulkEditModalProps) => {

	const sharedContent = useContentStore(state => state.sharedContent(state));
	const pricingContent = useContentStore(state => state.pricingContent(state));

	const [startDate, setStartDate] = useState(DateService.today());
	const [endDate, setEndDate] = useState(DateService.today());
	const [rate, setRate] = useState<EditableRate>(Object.assign({}, defaultRate));
	const [validation, setValidation] = useState({ isBookingCodeValid: false, isCostValid: true, isQuantityValid: true, isMinNightsValid: true } as EditableRateValidation);

	useEffect(() => {
		debug('props.isOpen changed...', props.isOpen);
		if (props.isOpen) {
			debug('doing something...');
			setRate(Object.assign({}, defaultRate));
			setStartDate(DateService.today());
			setEndDate(DateService.today());
			setValidation({ isBookingCodeValid: false, isCostValid: true, isQuantityValid: true, isMinNightsValid: true } as EditableRateValidation);
			debug('done something...');
		}
	}, [props.isOpen]);

	const onSubmitClicked = () => {
		if (!validation.isBookingCodeValid || !validation.isCostValid || !validation.isMinNightsValid || !validation.isQuantityValid) {
			NotificationService.error('Booking Code is required.');
			debug('onSubmitClicked', validation);
			debug('current rate', rate);
			return;
		}
		debug('startDate/endDate', startDate, endDate);
		props.closeDialog({ rate, startDate, endDate });
	}

	const onCancelClicked = () => {
		props.closeDialog();
	}

	const toggle = () => {
		debug('toggle called');
		props.closeDialog();
	}

	const handleTextChange = (newValue: string, event: React.ChangeEvent<HTMLElement>) => {
		let inputEvent = event as React.ChangeEvent<HTMLInputElement>;
		let indexableRate = Object.assign({}, rate) as IIndexable;

		indexableRate[inputEvent.target.name] = newValue;

		let updatedRate = indexableRate as EditableRate;
		if (inputEvent.target.name === 'quantity' && newValue && newValue.length > 0) {
			updatedRate.isFreeSell = false;
		}

		setRate(updatedRate);
	};

	const handleNumericChange = (newValue: string, event: React.ChangeEvent<HTMLElement>) => {
		let inputEvent = event as React.ChangeEvent<HTMLInputElement>;
		let indexableRate = Object.assign({}, rate) as IIndexable;

		indexableRate[inputEvent.target.name] = Number(newValue);

		debug('inputEvent.target.name', newValue);

		let updatedRate = indexableRate as EditableRate;
		if (inputEvent.target.name === 'quantity' && newValue && newValue.length > 0) {
			updatedRate.isFreeSell = false;
		}

		setRate(updatedRate);
	}

	const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		let indexableRate = Object.assign({}, rate) as IIndexable;
		debug('current', indexableRate);

		indexableRate[event.target.name] = event.target.checked;

		if (event.target.name === 'isFreeSell' && event.target.checked) {
			indexableRate['quantity'] = '';
		}

		let updatedRate = indexableRate as EditableRate;
		debug('updatedRate', updatedRate);

		setRate(updatedRate);
	}

	const updateValidation = (validationOpts: ValidationOptions) => {
		let newValidation = validation as IIndexable;

		newValidation[validationOpts.name] = validationOpts.value;
		debug('updateValidation (opts)', validationOpts);
		debug('updateValidation (old)', validation);
		debug('updateValidation (new)', newValidation);

		setValidation(newValidation as EditableRateValidation);
	}

	return (
		<Modal isOpen={props.isOpen} toggle={toggle} centered size='lg'>
			<ModalHeader>Bulk Edit Rates</ModalHeader>
			<ModalBody>
				<div id="bulkEditDialog">
					<div className="d-flex flex-row justify-content-center">
						<div className="mx-2">
							<div>
								<label>{pricingContent.startDateLabel}</label>
								<div className="">
									<DateSelector selectedDate={startDate} dateChanged={(date) => setStartDate(date)} />
								</div>
							</div>
						</div>
						<div className="mx-2">
							<div>
								<label>{pricingContent.endDateLabel}</label>
								<div className="">
									<DateSelector selectedDate={endDate} dateChanged={(date) => setEndDate(date)} />
								</div>
							</div>
						</div>
					</div>

					<div className="border border-secondary rounded gradient-gray p-3 mt-3">
						<div className="row">
							<div className="col-2"><label>{pricingContent.costTableHeader}</label></div>
							<div className="col">
								<div className="">
									<Textbox
										attributesInput={{
											name: 'cost',
											type: 'text',
											placeholder: 'Cost'
										}}
										value={rate.cost}
										onChange={handleNumericChange}
										onBlur={() => { }}
										validationOption={{
											type: 'number',
											numberType: 'price',
											mantissa: 2,
											required: false
										}}
										validationCallback={isInvalid => updateValidation({ name: 'isCostValid', value: !isInvalid } as ValidationOptions)}
									/>
								</div>
							</div>
						</div>
						<div className="row mt-2">
							<div className="col-2"><label>{pricingContent.bookingCodeTableHeader}</label></div>
							<div className="col">
								<div className="">
									<Textbox
										attributesInput={{
											name: 'bookingCode',
											type: 'text',
											placeholder: 'Booking Code'
										}}
										value={rate.bookingCode}
										onChange={handleTextChange}
										onBlur={() => { }}
										validationOption={{
											type: 'string',
											required: true,
											min: 1
										}}
										validationCallback={isInvalid => updateValidation({ name: 'isBookingCodeValid', value: !isInvalid } as ValidationOptions)}
									/>
								</div>
							</div>
						</div>
						<div className="row mt-2">
							<div className="col-2"><label>{pricingContent.quantityTableHeader}</label></div>
							<div className="col">
								<div className="">
									<Textbox
										attributesInput={{
											name: 'quantity',
											type: 'text',
											placeholder: 'Qty'
										}}
										value={rate.quantity}
										onChange={handleNumericChange}
										onBlur={() => { }}
										validationOption={{
											type: 'number',
											mantissa: 0,
											required: false
										}}
										validationCallback={isInvalid => updateValidation({ name: 'isQuantityValid', value: !isInvalid } as ValidationOptions)}
									/>
								</div>
							</div>
						</div>
						<div className="row mt-2">
							<div className="col-2"><label>{pricingContent.freeSellTableHeader}</label></div>
							<div className="col">
								<input type="checkbox" name="isFreeSell" checked={rate.isFreeSell} onChange={handleCheckChange} />
							</div>
						</div>
						<div className="row mt-2">
							<div className="col-2"><label>{pricingContent.soldOutTableHeader}</label></div>
							<div className="col">
								<input type="checkbox" name="isSoldOut" checked={rate.isSoldOut} onChange={handleCheckChange} />
							</div>
						</div>
						<div className="row mt-2">
							<div className="col-2"><label>{pricingContent.minNightsTableHeader}</label></div>
							<div className="col">
								<div className="">
									<Textbox
										attributesInput={{
											name: 'minNights',
											type: 'text',
											placeholder: 'Min. Nights'
										}}
										value={rate.minNights}
										onChange={handleNumericChange}
										onBlur={() => { }}
										validationOption={{
											type: 'number',
											mantissa: 0,
											required: false
										}}
										validationCallback={isInvalid => updateValidation({ name: 'isMinNightsValid', value: true } as ValidationOptions)}
									/>
								</div>
							</div>
						</div>
					</div>

					{/* <div className="row">
						<div className="col-12">
							<div className="d-flex flex-row pt-4 pt-md-5 justify-content-center">
								<button type="button" className="btn gradientOrange" onClick={onSubmitClicked}>{sharedContent.updateButton}</button>
								<button type="button" className="btn standard ms-2" onClick={onCancelClicked}>{sharedContent.cancelButton}</button>
							</div>
						</div>
					</div> */}
				</div>
			</ModalBody>
			<ModalFooter>
				<div className="row">
					<div className="col-12">
						<div className="d-flex flex-row justify-content-center">
							<button type="button" className="btn gradientOrange" onClick={onSubmitClicked}>{sharedContent.updateButton}</button>
							<button type="button" className="btn standard ms-2" onClick={onCancelClicked}>{sharedContent.cancelButton}</button>
						</div>
					</div>
				</div>
			</ModalFooter>
		</Modal>
	);
};

export { BulkEditModal }